import { Component, DestroyRef, Inject, OnInit, signal } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { collection, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';
import moment from 'moment';
import { User } from 'src/app/interfaces';
import { ConfirmChangeAvailabilityComponent } from 'src/app/schedule/dialogs/confirm-change-availability/confirm-change-availability.component';
import { db, functions } from 'src/app/app.component';
import { httpsCallable } from 'firebase/functions';
import { Role } from 'src/app/enums';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import { firstValueFrom } from 'rxjs';
import { MatInput } from '@angular/material/input';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-add-exception',
  templateUrl: './add-exception.component.html',
  styleUrls: ['./add-exception.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatError,
    MatSelect,
    MatOption,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatProgressSpinner,
  ],
})
export class AddExceptionComponent implements OnInit {
  exceptionForm: UntypedFormGroup = this.fb.group({});
  organisationId = localStorage.getItem('orgId');
  loading = signal(false);

  hours = Array.from({ length: 24 }, (_, i) => ({
    val: i,
    text: i.toString().padStart(2, '0'),
  }));

  minutes = Array.from({ length: 12 }, (_, i) => ({
    val: i * 5,
    text: (i * 5).toString().padStart(2, '0'),
  }));

  constructor(
    private fb: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: { userId: string; exception?: any },
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<AddExceptionComponent>,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.exceptionForm = this.fb.group({
      name: ['Niet beschikbaar', Validators.required],
      dateFrom: [, [Validators.required, this.futureDateValidator(true)]],
      dateTo: [, [Validators.required, this.futureDateValidator(false)]],
      startHour: [0, Validators.required],
      startMin: [0, Validators.required],
      endHour: [23, Validators.required],
      endMin: [55, Validators.required],
    });

    this.exceptionForm
      .get('startHour')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.exceptionForm.get('dateFrom')?.updateValueAndValidity();
      });
    this.exceptionForm
      .get('startMin')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.exceptionForm.get('dateFrom')?.updateValueAndValidity();
      });
    this.exceptionForm
      .get('endHour')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.exceptionForm.get('dateTo')?.updateValueAndValidity();
      });
    this.exceptionForm
      .get('endMin')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.exceptionForm.get('dateTo')?.updateValueAndValidity();
      });

    if (this.data.exception) {
      const ex = this.data.exception;
      this.exceptionForm.patchValue({
        name: ex.name,
        dateFrom: ex.dateFrom,
        dateTo: ex.dateTo,
        startHour: ex.dateFrom.getHours(),
        startMin: ex.dateFrom.getMinutes(),
        endHour: ex.dateTo.getHours(),
        endMin: ex.dateTo.getMinutes(),
      });
    }
  }

  private futureDateValidator(isFrom: boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const date = control.value;
      if (!date) {
        return null;
      }

      const form = control.parent;
      if (!form) {
        return null;
      }

      const hour = form.get(isFrom ? 'startHour' : 'endHour')?.value ?? 0;
      const min = form.get(isFrom ? 'startMin' : 'endMin')?.value ?? 0;

      const selectedDate = moment(date).hours(hour).minutes(min).seconds(0).milliseconds(0);
      const now = moment();

      if (selectedDate.isBefore(now)) {
        return { pastDate: true };
      }

      return null;
    };
  }

  async addException() {
    if (this.exceptionForm.invalid) {
      return;
    }

    const { name, dateFrom, dateTo, startHour, startMin, endHour, endMin } =
      this.exceptionForm.value;

    const newDateFrom = new Date(dateFrom);
    newDateFrom.setHours(startHour, startMin, 0, 0);

    const newDateTo = new Date(dateTo);
    newDateTo.setHours(endHour, endMin, 0, 0);

    if (newDateTo < newDateFrom) {
      this.snackBar.open('De begindatum mag niet later zijn dan de einddatum.', 'X', {
        duration: 5000,
      });
      return;
    }

    this.loading.set(true);

    try {
      const hasConflicts = await this.checkConflictingSchedule(newDateFrom, newDateTo);

      if (hasConflicts) {
        this.loading.set(false);
        const dialogRef = this.dialog.open(ConfirmChangeAvailabilityComponent, {
          width: '500px',
          disableClose: true,
          data: {
            availabilityType: 'exceptions',
            itemName: `${moment(newDateFrom).format('DD-MM-YYYY')} - ${moment(newDateTo).format('DD-MM-YYYY')}`,
          },
        });

        const result = await firstValueFrom(dialogRef.afterClosed());
        if (!result) {
          return;
        }
        this.loading.set(true);
      }

      await this.saveException(name, newDateFrom, newDateTo, true);
      this.dialogRef.close();
    } catch (error) {
      console.error(error);
      this.snackBar.open('Er is iets fout gegaan bij het opslaan.', 'X', { duration: 5000 });
      this.loading.set(false);
    }
  }

  private async checkConflictingSchedule(dateFrom: Date, dateTo: Date): Promise<boolean> {
    const daypartDays = await getDocs(
      query(
        collection(db, `organisations/${this.organisationId}/daypartDays`),
        where('year', '>=', dateFrom.getFullYear()),
        where('driverId', '==', this.data.userId),
      ),
    );

    if (daypartDays.empty) {
      return false;
    }

    return daypartDays.docs.some((daypartDay) => {
      const data = daypartDay.data();
      const rideDate = `${data.year}-${data.month + 1}-${data.day}`;
      return (
        moment(rideDate).isSameOrAfter(dateFrom, 'day') &&
        moment(rideDate).isSameOrBefore(dateTo, 'day')
      );
    });
  }

  private async saveException(name: string, dateFrom: Date, dateTo: Date, sendMail: boolean) {
    const userDocRef = doc(db, `organisations/${this.organisationId}/users/${this.data.userId}`);
    const userSnap = await getDoc(userDocRef);
    const user = userSnap.data() as User;

    if (!user) {
      return;
    }

    const saveObj = {
      dateFrom,
      dateTo,
      name,
    };

    const exceptions = user.exceptions ? [...user.exceptions] : [];
    const editIndex = this.data.exception?.index;

    if (editIndex !== undefined) {
      exceptions[editIndex] = saveObj;
    } else {
      exceptions.push(saveObj);
    }

    await setDoc(userDocRef, { exceptions }, { merge: true });

    this.snackBar.open(
      editIndex !== undefined ? 'Uitzondering aangepast.' : 'Uitzondering toegevoegd.',
      'X',
      { duration: 4000 },
    );

    if (sendMail) {
      await this.notifyPlanners(user, dateFrom, dateTo);
    }
  }

  private async notifyPlanners(user: User, dateFrom: Date, dateTo: Date) {
    try {
      const callBody = {
        orgId: this.organisationId,
        userName: 'planner',
        mailType: 'scheduleExceptions',
        emailTo: 'self',
        optionalData: {
          date: new Date(),
          changedBy: user.name,
          userChanged: user.name,
          userRole: user.roles?.includes(Role.Driver) ? 'chauffeur' : 'bijrijder',
          startTime: dateFrom,
          endTime: dateTo,
          mailPlanners: true,
          mailAdmins: true,
        },
      };
      const callable = httpsCallable(functions, 'httpSendMail');
      await callable(callBody);
    } catch (error) {
      console.error('Error sending mail:', error);
    }
  }
}
